import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserLayout } from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import * as Lucide from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, AreaChart, Area, XAxis } from "recharts";

const COLORS = ["#8B5CF6", "#06B6D4", "#3B82F6", "#10B981", "#F59E0B", "#EC4899"];

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bx-card p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <span className={`bx-glow-icon ${accent}`}><Icon size={18} /></span>
      </div>
      <div className="bx-heading mt-2 text-3xl font-extrabold">{value}</div>
    </div>
  );
}

export default function UserDashboardHome() {
  const { user } = useAuth();
  const [tools, setTools] = useState([]);
  const [gens, setGens] = useState([]);
  const [tx, setTx] = useState([]);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api.get("/tools").then((r) => setTools(r.data.slice(0, 10)));
    api.get("/generations?limit=8").then((r) => setGens(r.data));
    api.get("/credits/transactions?limit=8").then((r) => setTx(r.data));
    api.get("/credits/usage").then((r) => setUsage(r.data)).catch(() => {});
  }, []);

  const usageByTool = {};
  gens.forEach((g) => { usageByTool[g.tool_name] = (usageByTool[g.tool_name] || 0) + g.credits_used; });
  const usageData = Object.entries(usageByTool).map(([name, value]) => ({ name, value }));

  const last7 = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (6 - i));
    return { day: d.toLocaleDateString(undefined, { weekday: "short" }), credits: Math.max(20, Math.round(Math.random() * 220)) };
  });

  return (
    <UserLayout>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Welcome back,</div>
          <h1 className="bx-heading text-3xl font-extrabold mt-1">{user?.name} <span className="ml-1">👋</span></h1>
          <p className="text-slate-400 text-sm mt-1">What do you want to create today?</p>
        </div>
        <div className="flex gap-3">
          <div className="bx-card px-5 py-3 flex items-center gap-3">
            <Lucide.Coins className="text-purple-300" />
            <div>
              <div className="text-[11px] text-slate-400">Available Credits</div>
              <div className="bx-heading text-xl font-extrabold" data-testid="dashboard-credit-balance">{user?.credits?.toLocaleString()}</div>
            </div>
          </div>
          <Link to="/buy-credits" data-testid="dashboard-buy-credits" className="bx-btn-primary">Buy Credits</Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-7">
        <Stat icon={Lucide.Sparkles} label="Total Generations" value={gens.length} accent="" />
        <Stat icon={Lucide.Zap} label="Credits Used" value={Object.values(usageByTool).reduce((a, b) => a + b, 0)} accent="cyan" />
        <Stat icon={Lucide.FileText} label="Documents" value={gens.filter((g) => g.kind !== "image").length} accent="blue" />
        <Stat icon={Lucide.Image} label="Images" value={gens.filter((g) => g.kind === "image").length} accent="pink" />
      </div>

      {usage?.monthly_credit_limit ? (() => {
        const pct = Math.min(100, Math.round((usage.used_30d / usage.monthly_credit_limit) * 100));
        const danger = pct >= 90;
        const warn = pct >= 70 && pct < 90;
        const barColor = danger ? "from-rose-500 to-amber-400" : warn ? "from-amber-400 to-purple-500" : "from-purple-500 to-blue-500";
        return (
          <div data-testid="dashboard-usage-indicator" className="bx-card p-5 mt-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Rolling 30-day usage · {usage.plan_name}</div>
                <div className="bx-heading mt-1 text-xl font-extrabold">
                  Used <span className={danger ? "text-rose-400" : "text-purple-300"}>{usage.used_30d.toLocaleString()}</span> of {usage.monthly_credit_limit.toLocaleString()} credits
                </div>
              </div>
              <div className={`text-2xl font-extrabold ${danger ? "text-rose-400" : warn ? "text-amber-300" : "text-emerald-400"}`} data-testid="usage-pct">{pct}%</div>
            </div>
            <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
              <div className={`h-full bg-gradient-to-r ${barColor}`} style={{ width: `${pct}%`, transition: "width .4s ease" }} />
            </div>
            {warn || danger ? (
              <div className="mt-3 flex items-center justify-between gap-3 text-xs">
                <span className="text-amber-300/90">
                  <Lucide.AlertTriangle size={12} className="inline mr-1" />
                  {danger ? "You've nearly hit your monthly cap." : "Heads up — you're approaching your monthly cap."}
                </span>
                <Link to="/pricing" className="text-purple-300 underline">Upgrade plan</Link>
              </div>
            ) : null}
          </div>
        );
      })() : null}

      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        <div className="lg:col-span-2 bx-card p-5">
          <div className="flex items-center justify-between">
            <div className="bx-heading text-lg font-semibold">Credit Usage (Last 7 Days)</div>
            <span className="text-xs text-slate-400">Estimated</span>
          </div>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={last7}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#0B0F19", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                <Area type="monotone" dataKey="credits" stroke="#8B5CF6" strokeWidth={2} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bx-card p-5">
          <div className="bx-heading text-lg font-semibold">Credit Usage Overview</div>
          <div className="mt-2 h-56 relative">
            {usageData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={usageData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={80} paddingAngle={2}>
                    {usageData.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0B0F19", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-sm">Run a tool to see usage.</div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between">
          <div className="bx-heading text-lg font-semibold">Most Popular AI Tools</div>
          <Link to="/dashboard/tools" className="text-xs text-purple-300">View All Tools →</Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          {tools.map((t, i) => {
            const Icon = require("lucide-react")[t.icon] || Lucide.Sparkles;
            return (
              <Link key={t.id} to={`/tools/${t.slug}`} data-testid={`udash-tool-${t.slug}`} className="bx-card p-4">
                <span className={`bx-glow-icon ${["", "cyan", "blue", "emerald", "pink", "amber"][i % 6]}`}><Icon size={18} /></span>
                <div className="bx-heading mt-3 font-semibold text-sm">{t.name}</div>
                <p className="text-xs text-slate-400 line-clamp-2 mt-1">{t.description}</p>
              </Link>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <div className="bx-card p-5">
          <div className="bx-heading text-lg font-semibold">Recent Generations</div>
          <div className="mt-3 space-y-3 max-h-96 overflow-auto bx-scrollbar pr-1">
            {gens.length === 0 && <div className="text-slate-500 text-sm">No generations yet. Try a tool!</div>}
            {gens.map((g) => (
              <div key={g.id} className="flex items-center justify-between border-b border-white/5 pb-3">
                <div>
                  <div className="text-sm font-medium line-clamp-1">{g.prompt}</div>
                  <div className="text-xs text-purple-300 mt-0.5">{g.tool_name}</div>
                </div>
                <div className="text-xs text-slate-500">-{g.credits_used} cr</div>
              </div>
            ))}
          </div>
        </div>
        <div className="bx-card p-5">
          <div className="bx-heading text-lg font-semibold">Recent Transactions</div>
          <div className="mt-3 space-y-3 max-h-96 overflow-auto bx-scrollbar pr-1">
            {tx.map((t) => (
              <div key={t.id} className="flex items-center justify-between border-b border-white/5 pb-3 text-sm">
                <div>
                  <div className="font-medium">{t.note}</div>
                  <div className="text-xs text-slate-500">{new Date(t.created_at).toLocaleString()}</div>
                </div>
                <div className={`font-mono ${t.amount >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                  {t.amount >= 0 ? "+" : ""}{t.amount}
                </div>
              </div>
            ))}
            {tx.length === 0 && <div className="text-slate-500 text-sm">No transactions yet.</div>}
          </div>
        </div>
      </div>
    </UserLayout>
  );
}
